import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { OrgProvider } from './context/OrgContext';
import { WishlistProvider } from './context/WishlistContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoginModal from './components/LoginModal';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import WishlistPage from './pages/WishlistPage';
import OrdersPage from './pages/OrdersPage';
import LensSelectionPage from './pages/LensSelectionPage';
import AddressPage from './pages/AddressPage';
import CheckPage from './pages/CheckPage';
import ContactPage from './pages/ContactPage';
import ProfilePage from './pages/ProfilePage';
import SuccessPage from './pages/SuccessPage';
import TryonPage from './pages/TryonPage';
import TryonStudioPage from './pages/TryonStudioPage';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Sends a GA4 page_view on client-side route changes. gtag is loaded
// dynamically per-org in OrgProvider, so it may be undefined until then.
function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  React.useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: pathname + search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname, search]);
  return null;
}

const AppLayout: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  // /tryon-studio is a standalone, full-bleed experience — it renders without the
  // navbar and footer so the camera stage owns the whole viewport.
  const { pathname } = useLocation();
  const chromeless = pathname.startsWith('/tryon-studio');

  return (
    <>
      {!chromeless && <Navbar searchText={searchText} setSearchText={setSearchText} />}
      <main
        className="main-content"
        style={chromeless ? { minHeight: '100vh', position: 'relative' } : undefined}
      >
        <ScrollToTop />
        <AnalyticsTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/product/:id" element={<ProductDetailPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/order" element={<OrdersPage />} />
          <Route path="/check" element={<CheckPage />} />
          <Route path="/lens" element={<LensSelectionPage />} />
          <Route path="/address" element={<AddressPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/tryon" element={<TryonPage />} />
          <Route path="/tryon-studio" element={<TryonStudioPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      {!chromeless && <Footer />}
      <LoginModal />
    </>
  );
};

const App: React.FC = () => (
  <Router>
    <OrgProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <AppLayout />
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </OrgProvider>
  </Router>
);

export default App;
